import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { Plus, Trash2, Save, X, Calendar, Users, ChevronLeft, Headphones } from "lucide-react";
import RecordingPanel from "./RecordingPanel";

const empty = {
  title: "", description: "", image_url: "",
  speaker_name: "", speaker_title: "", speaker_bio: "", speaker_photo_url: "",
  date: "", start_time: "", end_time: "", timezone: "GMT",
  online: true, location: "Online", joining_info: "",
  capacity: 0, waitlist_enabled: true,
  visibility: "members", status: "draft", tag: "",
};

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">{label}</div>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
);

const Textarea = (props) => (
  <textarea {...props} rows={props.rows || 3} className="w-full px-3 py-2 rounded-md bg-white border text-[13px] font-sans" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
);

const Select = ({ options, ...rest }) => (
  <select {...rest} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const WebinarForm = ({ initial, onSubmit, onCancel, saving }) => {
  const [f, setF] = useState(initial || empty);
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setF(s => ({ ...s, [k]: v }));
  };
  const submit = (e) => { e.preventDefault(); onSubmit({ ...f, capacity: Number(f.capacity) || 0 }); };
  return (
    <form onSubmit={submit} className="rounded-lg p-6 bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="webinar-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[18px] font-medium">{initial?.id ? "Edit masterclass" : "New masterclass"}</h3>
        <button type="button" onClick={onCancel} className="text-[13px] link-underline inline-flex items-center gap-1"><X size={14} /> Cancel</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Title" full><Input required data-testid="webinar-title" value={f.title} onChange={set('title')} /></Field>
        <Field label="Description" full><Textarea data-testid="webinar-description" value={f.description} onChange={set('description')} /></Field>
        <Field label="Image URL"><Input data-testid="webinar-image-url" value={f.image_url} onChange={set('image_url')} /></Field>
        <Field label="Tag"><Input data-testid="webinar-tag" value={f.tag} onChange={set('tag')} placeholder="Investing, STEM…" /></Field>

        <Field label="Speaker name"><Input data-testid="webinar-speaker-name" value={f.speaker_name} onChange={set('speaker_name')} /></Field>
        <Field label="Speaker title"><Input data-testid="webinar-speaker-title" value={f.speaker_title} onChange={set('speaker_title')} /></Field>
        <Field label="Speaker bio" full><Textarea rows={2} value={f.speaker_bio} onChange={set('speaker_bio')} /></Field>
        <Field label="Speaker photo URL" full><Input value={f.speaker_photo_url} onChange={set('speaker_photo_url')} /></Field>

        <Field label="Date"><Input required type="date" data-testid="webinar-date" value={f.date} onChange={set('date')} /></Field>
        <Field label="Timezone"><Input value={f.timezone} onChange={set('timezone')} /></Field>
        <Field label="Start time"><Input required type="time" data-testid="webinar-start-time" value={f.start_time} onChange={set('start_time')} /></Field>
        <Field label="End time"><Input required type="time" data-testid="webinar-end-time" value={f.end_time} onChange={set('end_time')} /></Field>

        <Field label="Online?">
          <label className="inline-flex items-center gap-2 text-[13px] mt-2"><input type="checkbox" checked={!!f.online} onChange={set('online')} /> Online event</label>
        </Field>
        <Field label="Location"><Input value={f.location} onChange={set('location')} /></Field>
        <Field label="Joining info" full><Textarea rows={2} value={f.joining_info} onChange={set('joining_info')} placeholder="Zoom link, dial-in, etc." /></Field>

        <Field label="Capacity (0 = unlimited)"><Input type="number" min="0" data-testid="webinar-capacity" value={f.capacity} onChange={set('capacity')} /></Field>
        <Field label="Waitlist enabled">
          <label className="inline-flex items-center gap-2 text-[13px] mt-2"><input type="checkbox" checked={!!f.waitlist_enabled} onChange={set('waitlist_enabled')} /> Enable waitlist</label>
        </Field>

        <Field label="Visibility">
          <Select data-testid="webinar-visibility" value={f.visibility} onChange={set('visibility')} options={[{value:'public',label:'Public'},{value:'members',label:'Members only'}]} />
        </Field>
        <Field label="Status">
          <Select data-testid="webinar-status" value={f.status} onChange={set('status')} options={[{value:'draft',label:'Draft'},{value:'published',label:'Published'},{value:'completed',label:'Completed'},{value:'cancelled',label:'Cancelled'}]} />
        </Field>
      </div>
      <div className="mt-6 flex items-center gap-2">
        <button type="submit" disabled={saving} data-testid="webinar-save-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
      </div>
    </form>
  );
};

const CMSWebinars = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // 'list' | 'new' | 'edit' | 'regs'
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regs, setRegs] = useState([]);
  const [regEvent, setRegEvent] = useState(null);
  const [recEvent, setRecEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi.get('/admin/webinars'); setList(data); }
    catch { toast({ title: "Failed to load masterclasses" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    setSaving(true);
    try {
      await adminApi.post('/admin/webinars', payload);
      toast({ title: "Masterclass created" });
      setMode('list'); setEditing(null); load();
    } catch (err) { toast({ title: "Create failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };

  const update = async (payload) => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/webinars/${editing.id}`, payload);
      toast({ title: "Masterclass updated" });
      setMode('list'); setEditing(null); load();
    } catch (err) { toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This is permanent.`)) return;
    try {
      await adminApi.delete(`/admin/webinars/${w.id}`);
      toast({ title: "Deleted" }); load();
    } catch (err) {
      if (err?.response?.status === 409) {
        if (window.confirm(err.response.data.detail + "\n\nDelete anyway?")) {
          try { await adminApi.delete(`/admin/webinars/${w.id}?confirm=true`); toast({ title: "Deleted" }); load(); }
          catch (e2) { toast({ title: "Delete failed", description: e2?.response?.data?.detail || "Try again." }); }
        }
      } else toast({ title: "Delete failed", description: err?.response?.data?.detail || "Try again." });
    }
  };

  const openRegs = async (w) => {
    setRegEvent(w); setMode('regs');
    try { const { data } = await adminApi.get(`/admin/webinars/${w.id}/registrations`); setRegs(data); }
    catch { toast({ title: "Failed to load registrations" }); }
  };

  const promote = async (reg) => {
    try {
      await adminApi.post(`/admin/webinars/${regEvent.id}/promote/${reg.id}`);
      toast({ title: "Promoted from waitlist" });
      const { data } = await adminApi.get(`/admin/webinars/${regEvent.id}/registrations`);
      setRegs(data);
    } catch (err) { toast({ title: "Promote failed", description: err?.response?.data?.detail || "Try again." }); }
  };

  if (mode === 'new') return <WebinarForm onSubmit={create} onCancel={() => setMode('list')} saving={saving} />;
  if (mode === 'edit') return <WebinarForm initial={editing} onSubmit={update} onCancel={() => { setMode('list'); setEditing(null); }} saving={saving} />;
  if (mode === 'regs') return (
    <div className="rounded-lg p-6" style={{ background: '#fbf8ef' }}>
      <button onClick={() => setMode('list')} className="inline-flex items-center gap-1 text-[13px] mb-4 link-underline"><ChevronLeft size={14} /> Back to masterclasses</button>
      <h3 className="text-[20px] font-medium mb-4">Registrations · {regEvent?.title}</h3>
      {regs.length === 0 ? <div className="text-[13px] text-[#0d2a20]/60">No registrations yet.</div> : (
        <table className="w-full bg-white rounded-md overflow-hidden">
          <thead className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
            <tr><th className="text-left py-2 px-3">Member</th><th className="text-left py-2 px-3">Email</th><th className="text-left py-2 px-3">State</th><th className="text-left py-2 px-3">Registered</th><th className="text-left py-2 px-3">Actions</th></tr>
          </thead>
          <tbody>
            {regs.map(r => (
              <tr key={r.id} className="border-b last:border-b-0" style={{ borderColor: 'rgba(13,42,32,0.06)' }}>
                <td className="py-2 px-3 text-[13px]">{r.member_name || '—'}</td>
                <td className="py-2 px-3 text-[13px] text-[#0d2a20]/70">{r.member_email}</td>
                <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background:'#ece5d3' }}>{r.state}</span></td>
                <td className="py-2 px-3 text-[12px] text-[#0d2a20]/60">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2 px-3">
                  {r.state === 'waitlisted' && <button onClick={() => promote(r)} className="px-2 py-1 rounded btn-coral text-[11px]">Promote</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="rounded-lg p-6" style={{ background: '#fbf8ef' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[20px] font-medium">Masterclasses</h3>
        <button onClick={() => setMode('new')} data-testid="webinar-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]"><Plus size={14} /> New masterclass</button>
      </div>
      {loading ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">Loading…</div>
        : list.length === 0 ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">No masterclasses yet. Create one to get started.</div>
        : (
          <div className="overflow-x-auto bg-white rounded-md">
            <table className="w-full">
              <thead className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
                <tr>
                  <th className="text-left py-2 px-3">Title</th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Visibility</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Capacity</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(w => (
                  <tr key={w.id} className="border-b last:border-b-0 hover:bg-[#f6f0dc]/60" style={{ borderColor: 'rgba(13,42,32,0.06)' }}>
                    <td className="py-2 px-3 text-[13px] font-medium">{w.title}</td>
                    <td className="py-2 px-3 text-[12px] text-[#0d2a20]/70"><Calendar size={12} className="inline mr-1" />{w.date} · {w.start_time}</td>
                    <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background: w.visibility==='public' ? '#d6ecd6' : '#ece5d3' }}>{w.visibility}</span></td>
                    <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background: w.status==='published' ? '#d6ecd6' : '#fde5c7' }}>{w.status}</span></td>
                    <td className="py-2 px-3 text-[12px]">{w.capacity || '∞'} <span className="text-[#0d2a20]/50">({w.seats_taken || 0} taken)</span></td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openRegs(w)} className="p-1.5 rounded hover:bg-[#ece5d3]" title="Registrations"><Users size={14} /></button>
                        <button onClick={() => setRecEvent(w)} data-testid={`webinar-recording-${w.id}`} className="p-1.5 rounded hover:bg-[#ece5d3] inline-flex items-center gap-1 text-[11px]" title="Recording">
                          <Headphones size={14} />{w.recording && <span className="px-1.5 rounded" style={{ background: w.recording.published ? '#d6ecd6' : '#fde5c7' }}>{w.recording.published ? 'Live' : 'Draft'}</span>}
                        </button>
                        <button onClick={() => { setEditing(w); setMode('edit'); }} data-testid={`webinar-edit-${w.id}`} className="px-2 py-1 rounded btn-ghost text-[11px]">Edit</button>
                        <button onClick={() => remove(w)} className="p-1.5 rounded hover:bg-[#f3d6d1] text-[#7a1f13]" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      {recEvent && <RecordingPanel event={recEvent} onClose={() => setRecEvent(null)} onChanged={load} />}
    </div>
  );
};

export default CMSWebinars;
